import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { NotAvailableYet } from '@/components/NotAvailableYet';
import { US_STATES } from '@/components/USStates';
import { formatEIN, isValidEIN } from '@/utils/validation';
import { logMerchantDetailsChange } from '@/utils/activityLogger';
import { merchantHistoryQueryKey } from '@/hooks/useMerchantHistory';
import { MerchantRecord } from '@/shared/types/merchant';
import { parseRevenue } from '@/utils/organization/revenue';
import { EditButton } from './EditButton';
import { SettingsSection } from './SettingsGroup';
import { MERCHANT_CONTACTS_SECTION_ID } from './MerchantContactsSection';

// Merchant Details (FIG-320 / REQ-039): the Add Merchant fields, edited in
// place. Saves only these columns, never the onboarding status. The Merchant
// Acronym (REQ-052) needs a new column (UC-218).
// FIG-345 · UC-228: the Point of Contact is shown read-only here. It is the
// merchant's sign-in email, so it changes only in Merchant Contacts, which
// keeps organisations.poc_* in step with the contacts list.

interface MerchantDetailsSectionProps {
  merchant: MerchantRecord;
  canEdit: boolean;
  onUpdated: () => void;
}

interface DetailsDraft {
  name: string;
  name_dbas: string;
  state_of_incorporation: string;
  ein: string;
  address: string;
  revenue_last_12_months: string;
}

type DraftField = keyof DetailsDraft;

const FIELD_LABELS: Record<DraftField, string> = {
  name: 'Company Name',
  name_dbas: 'DBAs (doing business as)',
  state_of_incorporation: 'State of Incorporation',
  ein: 'EIN',
  address: 'Company Address',
  revenue_last_12_months: 'Revenue (last 12 months, as stated by merchant)',
};

const REQUIRED: DraftField[] = ['name', 'state_of_incorporation', 'ein', 'address'];

const toDraft = (m: MerchantRecord): DetailsDraft => ({
  name: m.name ?? '',
  name_dbas: m.name_dbas ?? '',
  state_of_incorporation: m.state_of_incorporation ?? '',
  ein: m.ein ?? '',
  address: m.address ?? '',
  revenue_last_12_months: m.revenue_last_12_months == null ? '' : String(m.revenue_last_12_months),
});

const formatRevenue = (value: MerchantRecord['revenue_last_12_months']): string => {
  const text = String(value ?? '').replace(/[$,]/g, '').trim();
  const parsed = typeof value === 'number' ? value : text ? Number(text) : null;
  return parsed == null || !Number.isFinite(parsed)
    ? 'Not provided'
    : parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const stateLabel = (code: string | null | undefined) =>
  US_STATES.find((s) => s.value === code)?.label ?? code ?? '';

function validateDetails(draft: DetailsDraft): Partial<Record<DraftField, string>> {
  const errors: Partial<Record<DraftField, string>> = {};
  REQUIRED.forEach((field) => {
    if (!draft[field].trim()) errors[field] = `${FIELD_LABELS[field]} is required`;
  });
  if (draft.ein.trim() && !isValidEIN(draft.ein)) errors.ein = 'Valid EIN format required (XX-XXXXXXX)';
  // Empty clears the revenue; anything else must be a valid amount (never silently changed).
  if (draft.revenue_last_12_months.trim() && parseRevenue(draft.revenue_last_12_months) === null) {
    errors.revenue_last_12_months = 'Enter an amount such as 250000 or 250,000.00';
  }
  return errors;
}

export const MerchantDetailsSection: React.FC<MerchantDetailsSectionProps> = ({ merchant, canEdit, onUpdated }) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DetailsDraft>(() => toDraft(merchant));
  // Values when editing started; a refetch mid-edit must not count as a change.
  const [baseline, setBaseline] = useState<DetailsDraft>(() => toDraft(merchant));
  const [errors, setErrors] = useState<Partial<Record<DraftField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<false | 'saved' | 'not-recorded'>(false);

  // "Saved" shows for a few seconds after saving (REQ-025).
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(timer);
  }, [saved]);

  const startEdit = () => {
    setDraft(toDraft(merchant));
    setBaseline(toDraft(merchant));
    setErrors({});
    setSaveError(null);
    setSaved(false);
    setEditing(true);
  };

  const update = (field: DraftField) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = field === 'ein' ? formatEIN(e.target.value) : e.target.value;
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const changedFields = (Object.keys(FIELD_LABELS) as DraftField[]).filter((f) => draft[f].trim() !== baseline[f].trim());

  const save = async () => {
    const validation = validateDetails(draft);
    if (Object.values(validation).some(Boolean)) {
      setErrors(validation);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (changedFields.includes('ein')) {
        const { data, error } = await invokeFunction(supabase, 'validate-ein-duplicate-v2', {
          body: { ein: draft.ein, organization_id: merchant.id },
        });
        if (error) throw error;
        if ((data as { isDuplicate?: boolean } | null)?.isDuplicate) {
          setErrors({ ein: 'This EIN is already used by another merchant' });
          return;
        }
      }

      const { error } = await supabase
        .from('organisations')
        .update({
          name: draft.name.trim(),
          name_dbas: draft.name_dbas.trim(),
          state_of_incorporation: draft.state_of_incorporation,
          ein: draft.ein.trim(),
          address: draft.address.trim(),
          revenue_last_12_months: parseRevenue(draft.revenue_last_12_months),
        })
        .eq('id', merchant.id);
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      let recorded = false;
      if (user?.email) {
        recorded = await logMerchantDetailsChange(
          user.email,
          merchant.id,
          draft.name.trim(),
          changedFields.map((f) => FIELD_LABELS[f]),
          user.id,
        );
      }
      await queryClient.invalidateQueries({ queryKey: merchantHistoryQueryKey(merchant.id) });
      setEditing(false);
      setSaved(recorded ? 'saved' : 'not-recorded');
      onUpdated();
    } catch (error) {
      console.error('Failed to save merchant details', error);
      setSaveError('Details not saved. Please try again or contact support. The previous details were kept.');
    } finally {
      setSaving(false);
    }
  };

  const dash = (value: string | null | undefined) => (value && value.trim() ? value : '—');

  return (
    <SettingsSection
      title="Merchant Details"
      action={
        <div className="flex items-center gap-2">
          {saved === 'saved' && <span role="status" className="text-sm text-emerald-700">Saved</span>}
          {saved === 'not-recorded' && (
            <span role="status" className="text-sm text-amber-800">Saved, but not recorded in the merchant's history</span>
          )}
          {canEdit && !editing && <EditButton label="Edit merchant details" onClick={startEdit} />}
        </div>
      }
    >
      {!editing ? (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <div><dt className="text-sm text-muted-foreground">Company Name</dt><dd className="mt-1">{merchant.name}</dd></div>
          <div>
            <dt className="text-sm text-muted-foreground">Merchant Acronym</dt>
            <dd className="mt-1"><NotAvailableYet useCase="UC-218" /></dd>
          </div>
          <div><dt className="text-sm text-muted-foreground">{FIELD_LABELS.name_dbas}</dt><dd className="mt-1">{dash(merchant.name_dbas)}</dd></div>
          <div><dt className="text-sm text-muted-foreground">{FIELD_LABELS.state_of_incorporation}</dt><dd className="mt-1">{dash(stateLabel(merchant.state_of_incorporation))}</dd></div>
          <div><dt className="text-sm text-muted-foreground">EIN</dt><dd className="mt-1 font-mono text-sm">{dash(merchant.ein)}</dd></div>
          <div><dt className="text-sm text-muted-foreground">{FIELD_LABELS.address}</dt><dd className="mt-1">{dash(merchant.address)}</dd></div>
          <div><dt className="text-sm text-muted-foreground">{FIELD_LABELS.revenue_last_12_months}</dt><dd className="mt-1">{formatRevenue(merchant.revenue_last_12_months)}</dd></div>
          <PointOfContact merchant={merchant} />
        </dl>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {(['name', 'name_dbas'] as DraftField[]).map((field) => (
              <DetailsInput key={field} field={field} draft={draft} errors={errors} onChange={update(field)} />
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="md-state_of_incorporation">{FIELD_LABELS.state_of_incorporation} *</Label>
              <select
                id="md-state_of_incorporation"
                value={draft.state_of_incorporation}
                onChange={update('state_of_incorporation')}
                aria-invalid={!!errors.state_of_incorporation}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select a State</option>
                {US_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {errors.state_of_incorporation && <p className="text-xs text-destructive">{errors.state_of_incorporation}</p>}
            </div>
            {(['ein', 'address', 'revenue_last_12_months'] as DraftField[]).map((field) => (
              <DetailsInput key={field} field={field} draft={draft} errors={errors} onChange={update(field)} />
            ))}
          </div>
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <PointOfContact merchant={merchant} />
          </dl>
          {saveError && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{saveError}</div>}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={save} disabled={saving || changedFields.length === 0}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save details
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <span className="text-sm text-muted-foreground">Changes are recorded in the merchant's history.</span>
          </div>
        </div>
      )}
    </SettingsSection>
  );
};

/** Read-only POC name and email, with a link to where they change (REQ-039). */
const PointOfContact: React.FC<{ merchant: MerchantRecord }> = ({ merchant }) => {
  const dash = (value: string | null | undefined) => (value && value.trim() ? value : '—');
  const goToContacts = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(MERCHANT_CONTACTS_SECTION_ID);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <>
      <div><dt className="text-sm text-muted-foreground">Point of Contact</dt><dd className="mt-1">{dash(merchant.poc_name)}</dd></div>
      <div>
        <dt className="text-sm text-muted-foreground">POC Email</dt>
        <dd className="mt-1">{dash(merchant.poc_email)}</dd>
        <dd className="mt-1 text-xs">
          <a href={`#${MERCHANT_CONTACTS_SECTION_ID}`} onClick={goToContacts} className="text-primary underline-offset-2 hover:underline">
            Change in Merchant Contacts
          </a>
        </dd>
      </div>
    </>
  );
};

const DetailsInput: React.FC<{
  field: DraftField;
  draft: DetailsDraft;
  errors: Partial<Record<DraftField, string>>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ field, draft, errors, onChange }) => {
  const id = `md-${field}`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{FIELD_LABELS[field]}{REQUIRED.includes(field) ? ' *' : ''}</Label>
      <Input
        id={id}
        type="text"
        inputMode={field === 'revenue_last_12_months' ? 'decimal' : undefined}
        value={draft[field]}
        onChange={onChange}
        aria-invalid={!!errors[field]}
      />
      {errors[field] && <p className="text-xs text-destructive">{errors[field]}</p>}
    </div>
  );
};

export default MerchantDetailsSection;
